import { Modal } from '@byecode/ui'
import type { DataSourceAbstract, SelectDataSourceVariable, VariableADTvalue } from '@lighthouse/core'
import { DEVICE_ID_VALUE, VariableType } from '@lighthouse/core'
import cls from 'classnames'
import React, { forwardRef, useCallback, useMemo, useState } from 'react'
import { makeMatchPI } from 'ts-adt/MakeADT'

import { FieldInnerTypeOfDataTypeNameMap, variableSystemNameMap } from '../../../constants'
import { innerTypeToMethods, methodList } from '../../EditorConfigViewRenderer/FormulaValueEditor/constant'
import { FormulaView } from '../../FormulaView'
import { generateText, isEmptyRichTextValue } from '../../RichText'
import { imageVariableNameMap } from '../constant'
import type { VariableTree2 } from '../types'
import * as SC from './styles'

export interface VariableTagProps {
    value?: VariableADTvalue
    pageOptions?: VariableTree2[]
    upstreamOptions?: VariableTree2[]
    userOption?: VariableTree2
    dateOption?: VariableTree2
    inputOption?: VariableTree2
    filterOption?: VariableTree2
    datasourceOption?: VariableTree2
    datasourceOptions?: VariableTree2[]
    fieldOptions?: VariableTree2[]
    formOption?: VariableTree2
    viewOptions?: VariableTree2[]
    eventOption?: VariableTree2
    loopOption?: VariableTree2
    filterOptions?: VariableTree2[]
    imageOption?: VariableTree2
    dataSourceList?: DataSourceAbstract[]
    withClose?: boolean
    isSelected?: boolean
    style?: React.CSSProperties
    onClose?: () => void
    onOpenSelectDataSourceModal?: (val: SelectDataSourceVariable) => void
    onChangeVariableValue?: (val: VariableADTvalue) => void
}

function findOptionPath(valuePath: string[], tree?: VariableTree2[]) {
    if (!tree) {
        return
    }

    function curr(options: VariableTree2[], paths: string[]): string[] | undefined {
        for (const option of options) {
            const path = paths[0]
            if (option.value === path) {
                if (option.children) {
                    const res = curr(option.children, paths.slice(1))
                    if (res && res.length > 0) {
                        const dataTypeName = option.fieldInnerType ? FieldInnerTypeOfDataTypeNameMap[option.fieldInnerType] : ''
                        return [`${option.label}`, ...res]
                    }
                }
                const dataTypeName = option.fieldInnerType ? FieldInnerTypeOfDataTypeNameMap[option.fieldInnerType] : ''
                return paths.length === 1 ? [`${option.label}{${dataTypeName}}`] : undefined
            }
        }
    }

    return curr(tree, valuePath)
}

export const VariableTag = forwardRef<HTMLElement, VariableTagProps>(
    (
        {
            value: data,
            formOption,
            pageOptions,
            upstreamOptions,
            userOption,
            dateOption,
            viewOptions,
            eventOption,
            loopOption,
            inputOption,
            filterOption,
            imageOption,
            // filterOptions,
            datasourceOption,
            datasourceOptions,
            fieldOptions,
            withClose,
            isSelected,
            dataSourceList,
            style,
            onOpenSelectDataSourceModal,
            onChangeVariableValue,
            onClose,
            ...rest
        },
        ref
    ) => {
        const [openFormulaModal, setOpenFormulaModal] = useState(false)
        const tooltip = useCallback(
            (value: VariableADTvalue | undefined): string | undefined => {
                if (!value) {
                    return
                }

                return makeMatchPI('type')(value)(
                    {
                        [VariableType.VALUE]: v => {
                            return undefined
                        },
                        [VariableType.EVENT]: v => {
                            if (!eventOption || !v.eventVariable?.value) {
                                return
                            }
                            const path = findOptionPath([v.eventVariable.value], eventOption.children)
                            if (!path) {
                                return
                            }
                            return `${eventOption.label}/${path.join('/')}`
                        },
                        [VariableType.LOOP]: v => {
                            if (!loopOption || !v.loopVariable?.value) {
                                return
                            }
                            const path = findOptionPath([v.loopVariable.value], loopOption.children)
                            if (!path) {
                                return
                            }
                            return `${loopOption.label}/${path.join('/')}`
                        },
                        [VariableType.FIELD_ID]: v => {
                            if (!v.fieldIdVariable?.fieldId) {
                                return
                            }
                            if (datasourceOption) {
                                const path = findOptionPath([datasourceOption.value, v.fieldIdVariable.fieldId], [datasourceOption])
                                if (!path) {
                                    return
                                }
                                return path.join('/')
                            }
                            if (fieldOptions) {
                                const path = findOptionPath([v.fieldIdVariable.fieldId], fieldOptions)
                                if (!path) {
                                    return
                                }
                                return path.join('/')
                            }
                            return
                        },
                        [VariableType.FILTER]: v => {
                            if (!filterOption || !v.filterVariable?.blockId || !v.filterVariable.itemId) {
                                return
                            }
                            const path = findOptionPath(
                                ['FILTER_VARIABLE', v.filterVariable.blockId, v.filterVariable.itemId],
                                [filterOption]
                            )
                            if (!path) {
                                return
                            }
                            return path.join('/')
                        },
                        [VariableType.FORM]: v => {
                            if (!formOption || !v.formVariable?.fieldId) {
                                return
                            }
                            const path = findOptionPath([v.formVariable.fieldId], formOption.children)
                            if (!path) {
                                return
                            }
                            return `${formOption.label}/${path.join('/')}`
                        },
                        [VariableType.INPUT]: v => {
                            if (!inputOption || !v.inputVariable?.blockId) {
                                return
                            }

                            const path = findOptionPath([v.inputVariable.blockId], inputOption.children)
                            if (!path) {
                                return
                            }

                            return `${inputOption.label}/${path.join('/')}`
                        },
                        [VariableType.PAGE_DATA_QUERY]: v => {
                            if (!pageOptions || !v.pageDataQueryVariable?.id || !v.pageDataQueryVariable.itemId) {
                                return
                            }

                            const path = findOptionPath([v.type, v.pageDataQueryVariable.id, v.pageDataQueryVariable.itemId], pageOptions)
                            if (!path) {
                                return
                            }

                            return path.join('/')
                        },
                        [VariableType.PAGE_LINK]: v => {
                            return v.pageLinkVariable?.value === 'CURRENT_PAGE' ? '当前页面链接' : ''
                        },
                        [VariableType.DEVICE_ID]: v => {
                            return v.deviceIdVariable?.value === DEVICE_ID_VALUE ? '设备ID' : ''
                        },
                        [VariableType.ROUTE_PATH]: v => {
                            if (!pageOptions || !v.routePathVariable?.id) {
                                return
                            }

                            const path = findOptionPath([v.type, v.routePathVariable.id], pageOptions)
                            if (!path) {
                                return
                            }

                            return path.join('/')
                        },
                        [VariableType.ROUTE_QUERY]: v => {
                            if (!pageOptions || !v.routeQueryVariable?.id) {
                                return
                            }

                            const path = findOptionPath([v.type, v.routeQueryVariable.id], pageOptions)
                            if (!path) {
                                return
                            }

                            return path.join('/')
                        },
                        [VariableType.PAGE_STATE]: v => {
                            if (!pageOptions || !v.pageStateVariable?.id) {
                                return
                            }
                            const path = findOptionPath([v.type, v.pageStateVariable.id], pageOptions)
                            if (!path) {
                                return
                            }

                            return path.join('/')
                        },
                        [VariableType.SELECT_DATASOURCE]: v => {
                            const ds = dataSourceList?.find(item => item.id === v.selectDataSourceVariable?.dsId)
                            if (!ds || !v.selectDataSourceVariable?.fieldId) {
                                return
                            }
                            return `查询数据表：${ds.name}`
                        },

                        [VariableType.SYSTEM]: v => {
                            if (!v.systemVariable?.value) {
                                return ''
                            }

                            return `常用日期/${variableSystemNameMap[v.systemVariable.value]}`
                        },
                        [VariableType.ARG_VARIABLE]: v => {
                            if (!upstreamOptions || !v.argVariable?.nodeId || !v.argVariable.argId) {
                                return
                            }

                            const path = findOptionPath([v.argVariable.nodeId, v.argVariable.argId], upstreamOptions)
                            if (!path) {
                                return
                            }

                            return path.join('/')
                        },
                        [VariableType.UPSTREAM]: v => {
                            if (!upstreamOptions || !v.upstreamVariable?.nodeId || !v.upstreamVariable.fieldId) {
                                return
                            }

                            const path = findOptionPath([v.upstreamVariable.nodeId, v.upstreamVariable.fieldId], upstreamOptions)
                            if (!path) {
                                return
                            }

                            return path.join('/')
                        },

                        [VariableType.USER]: v => {
                            if (!userOption || !v.userVariable?.fieldId) {
                                return
                            }
                            const path = findOptionPath([v.userVariable.fieldId], userOption.children)
                            if (!path) {
                                return
                            }
                            return `${userOption.label}/${path.join('/')}`
                        },
                        [VariableType.VARIABLE]: v => {
                            if (!datasourceOptions || !v.fieldVariable?.dsId || !v.fieldVariable.fieldId) {
                                return
                            }
                            const path = findOptionPath([v.fieldVariable.dsId, v.fieldVariable.fieldId], datasourceOptions)
                            if (!path) {
                                return
                            }
                            return path.join('/')
                        },
                        [VariableType.VIEW]: v => {
                            if (!viewOptions || viewOptions.length === 0 || !v.viewVariable?.fieldId) {
                                return
                            }
                            // 尝试在所有视图中查找匹配的字段
                            for (const viewOption of viewOptions) {
                                const path = findOptionPath([v.viewVariable.fieldId], viewOption.children)
                                if (path) {
                                    return `${viewOption.label}/${path.join('/')}`
                                }
                            }
                            return undefined
                        },
                        [VariableType.FORMULA]: v => {
                            const formulaValue = v.formulaVariable?.value
                            const isEmptyFormula = !formulaValue || isEmptyRichTextValue(formulaValue, { currentValueMark: true })
                            if (isEmptyFormula) {
                                return '点击编辑'
                            }
                            return generateText(formulaValue, {
                                variable: {
                                    renderLabel: node => `{${tooltip(node.attrs.value as VariableADTvalue | undefined) ?? '找不到变量'}}`
                                },
                                currentValueMark: true
                            })
                        },
                        [VariableType.IMAGE]: v => {
                            if (!imageOption || !v.imageVariable?.dataKey) {
                                return
                            }
                            return `${imageOption.label}/${imageVariableNameMap[v.imageVariable.dataKey]}`
                        }
                    },
                    () => {
                        return ''
                    }
                )
            },
            [
                dataSourceList,
                datasourceOption,
                datasourceOptions,
                eventOption,
                fieldOptions,
                filterOption,
                formOption,
                imageOption,
                inputOption,
                loopOption,
                pageOptions,
                upstreamOptions,
                userOption,
                viewOptions
            ]
        )

        const errTagEle = useMemo(
            () => (
                <SC.ErrorTag>
                    <SC.Text color="var(--color-red-500)">找不到变量</SC.Text>
                    {withClose && <SC.Icon type="Close" color="var(--color-red-500)" size={12} onClick={onClose} />}
                </SC.ErrorTag>
            ),
            [onClose, withClose]
        )

        const formulaValue = useMemo(() => {
            if (!data) {
                return undefined
            }
            if (data.type === VariableType.FORMULA) {
                return data.formulaVariable?.value
            }
            return undefined
        }, [data])

        const text = useMemo(() => {
            if (!data) {
                return
            }
            return tooltip(data)
        }, [data, tooltip])

        const isError = useMemo(() => {
            if (!text) {
                return false
            }
            return text.includes('{找不到变量}')
        }, [text])

        const ele = useMemo(() => {
            if (!data) {
                return errTagEle
            }
            if (!text) {
                return errTagEle
            }

            if (data.type === VariableType.SELECT_DATASOURCE) {
                return (
                    <span
                        onClick={e => {
                            e.stopPropagation()
                            onOpenSelectDataSourceModal?.(data)
                        }}
                    >
                        <SC.Text>{text}</SC.Text>
                        <SC.Icon type="edit" size={12} color="var(--color-main)" />
                    </span>
                )
            }

            if (data.type === VariableType.FORMULA) {
                return (
                    <span
                        onClick={e => {
                            e.stopPropagation()
                            setOpenFormulaModal(true)
                        }}
                    >
                        <SC.VariableIcon color={isError ? 'var(--color-red-500)' : undefined} type="PropertyFormula" size={14} />
                        <SC.Text color={isError ? 'var(--color-red-500)' : undefined}>{text}</SC.Text>
                        {withClose && <SC.Icon type="Close" size={12} onClick={onClose} />}
                    </span>
                )
            }

            return (
                <>
                    <SC.Text>{text}</SC.Text>
                    {withClose && <SC.Icon type="Close" size={12} onClick={onClose} />}
                </>
            )
        }, [data, errTagEle, isError, onClose, onOpenSelectDataSourceModal, text, withClose])

        return (
            <>
                <SC.TagContainer ref={ref} style={style} {...rest}>
                    <SC.TagWrapper className={cls({ selected: isSelected, error: isError })}>{ele}</SC.TagWrapper>
                </SC.TagContainer>
                <Modal
                    title="公式编辑"
                    width={1000}
                    styles={{ desktop: { body: { padding: 0 } } }}
                    open={openFormulaModal}
                    onClose={() => setOpenFormulaModal(false)}
                >
                    <FormulaView
                        zIndex={201}
                        value={formulaValue}
                        pageOptions={pageOptions}
                        upstreamOptions={upstreamOptions}
                        userOption={userOption}
                        formOption={formOption}
                        dateOption={dateOption}
                        viewOptions={viewOptions}
                        inputOption={inputOption}
                        filterOption={filterOption}
                        datasourceOption={datasourceOption}
                        fieldOptions={fieldOptions}
                        methodList={methodList}
                        innerTypeToMethods={innerTypeToMethods}
                        onClose={() => setOpenFormulaModal(false)}
                        onOk={json => {
                            onChangeVariableValue?.({ type: VariableType.FORMULA, formulaVariable: { value: json || {} } })
                            setOpenFormulaModal(false)
                        }}
                    />
                </Modal>
            </>
        )
    }
)
